import { Typography } from '@mui/material';
import { clsx } from 'clsx';
import React, { memo, useCallback, useMemo, useState } from 'react';

import chart from '~/assets/icons/chart.svg';
import chartMarker from '~/assets/icons/chart-marker.svg';
import { AskingValuation } from '~/pages/Company/components/Inference/components/AskingValuation';
import { ChartPopover } from '~/pages/Company/components/Inference/components/ChartPopover';
import { GetCompanyInferenceResponse } from '~/services/rtkQuery/company/types';

import styles from './styles.module.scss';

const NAMES: Record<
  GetCompanyInferenceResponse['result']['data']['series_c_pricing']['graph_mode'],
  string
> = {
  fair: 'Fair Pricing',
  advantageous: 'Advantageous Pricing',
  overpriced: 'Overpriced',
};

interface Props {
  askingPrice: number;
  predYPercent: number;
  askingYPercent: number;
  predValue: number;
  data: GetCompanyInferenceResponse['result']['data']['series_c_pricing'];
  barChartData: GetCompanyInferenceResponse['result']['data']['supporting_graphs']['series_c_pricing'];
}

const Component: React.FC<Props> = ({
  askingPrice,
  predYPercent,
  askingYPercent,
  predValue,
  data,
  barChartData,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleChartButtonClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(event => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleChartPopoverClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const askingPricePosition = useMemo(() => {
    const halfWidthPercent = 50;
    const areaHalfWidthPercent = 20;
    const priceMarkerHalfWidth = 50;
    const outerOffsetPercent = 5;
    const innerOffsetPercent = 15;

    if (askingPrice < data.price_low_th) {
      return {
        left: `calc(${
          halfWidthPercent - areaHalfWidthPercent - outerOffsetPercent
        }% - ${priceMarkerHalfWidth}px)`,
      };
    }

    if (askingPrice >= data.price_low_th && askingPrice <= data.fv) {
      return {
        left: `calc(${halfWidthPercent - innerOffsetPercent}% - ${priceMarkerHalfWidth}px)`,
      };
    }

    if (askingPrice >= data.fv && askingPrice <= data.price_high_th) {
      return {
        left: `calc(${halfWidthPercent + innerOffsetPercent}% - ${priceMarkerHalfWidth}px)`,
      };
    }

    if (askingPrice > data.price_high_th) {
      return {
        left: `calc(${
          halfWidthPercent + areaHalfWidthPercent + outerOffsetPercent
        }% - ${priceMarkerHalfWidth}px)`,
      };
    }

    return undefined;
  }, [askingPrice, data.fv, data.price_high_th, data.price_low_th]);

  return (
    <div className={styles.container}>
      <div className={styles.container__heading}>
        <Typography color="var(--text-3-color)" fontWeight="500" variant="subtitle1">
          Series C Price
        </Typography>
        <div className={styles.container__confidence}>
          <Typography
            alignItems="center"
            color="var(--text-3-color)"
            display="flex"
            mr="8px"
            variant="subtitle1"
          >
            Confidence level:
            <Typography
              color={data.confidence === 'high' ? 'var(--success-color)' : 'var(--error-color)'}
              component="span"
              fontWeight="700"
              variant="subtitle1"
            >
              {data.confidence === 'high' ? 'HIGH' : 'LOW'}*
            </Typography>
          </Typography>
          <button type="button" onClick={handleChartButtonClick}>
            <img alt="chart" src={chart} />
          </button>
          <ChartPopover anchorEl={anchorEl} onClose={handleChartPopoverClose}>
            <AskingValuation
              askingPrice={askingPrice}
              askingYPercent={askingYPercent}
              className={styles['container__bar-chart']}
              data={barChartData}
              predValue={predValue}
              predYPercent={predYPercent}
            />
          </ChartPopover>
        </div>
      </div>
      <div className={styles.chart}>
        <div
          className={clsx(styles.chart__bar, {
            [styles['chart__bar--fair']]: data.graph_mode === 'fair',
            [styles['chart__bar--advantageous']]: data.graph_mode === 'advantageous',
            [styles['chart__bar--overpriced']]: data.graph_mode === 'overpriced',
          })}
        >
          <div className={styles.chart__area} />
          <div className={styles.chart__label}>
            <Typography align="center" color="var(--text-3-color)" fontWeight="600" variant="h6">
              {NAMES[data.graph_mode]}
            </Typography>
          </div>
        </div>
        <div className={styles['chart__x-axis-line']} />
        <div className={styles.chart__price} style={askingPricePosition}>
          <Typography
            align="center"
            color="var(--text-3-color-60)"
            mb="7px"
            variant="caption"
            width="32px"
          >
            Asking Price
          </Typography>
          <img alt="chartMarker" className={styles['chart__chart-marker']} src={chartMarker} />
          <Typography color="var(--text-3-color)" variant="subtitle1">
            ${askingPrice.toFixed(0)}M
          </Typography>
        </div>
        <div className={styles.chart__price} style={{ left: 'calc(50% - 50px)' }}>
          <Typography
            align="center"
            color="var(--text-3-color-60)"
            mb="7px"
            variant="caption"
            width="38px"
          >
            Ada Fair Price
          </Typography>
          <img alt="chartMarker" className={styles['chart__chart-marker']} src={chartMarker} />
          <Typography color="var(--text-3-color)" fontWeight="700" variant="subtitle1">
            ${data.fv.toFixed(0)}M
          </Typography>
        </div>
      </div>
    </div>
  );
};

export const SeriesCPrice = memo(Component);
